import React, { useEffect, useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'localization';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import {
  ContextInventoryOrganism,
  CustomDialog,
  LocalizedInputAtom,
  Loader,
  useApiClientsOrNull,
  confirmDialogAtom,
  ConfirmDialogAtom,
} from 'design-system/my-lib';
import type { LocalizedValue } from 'design-system/my-lib';
import Header from '../Header/Header';
import type { RootState, AppDispatch } from '@center-pwa/store/store';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import type { Context, LocalizedString } from 'types/context';
import {
  fetchContexts,
  deleteContextThunk,
  cloneContextThunk,
  selectContextsList,
  selectContextsLoading,
  selectContextsError,
  clearContextsError,
} from '@center-pwa/reducers/contextsReducer';
import logo from '../../../assets/images/center-icon.png';
import './ContextListPage.scss';

// Lazy load mobile components
const MobileHeader = lazy(() =>
  import('design-system/my-lib').then((m) => ({ default: m.MobileHeader }))
);

const MobileNavBar = lazy(() =>
  import('design-system/my-lib').then((m) => ({ default: m.MobileNavBar }))
);

interface CloneDialogState {
  visible: boolean;
  sourceContext: Context | null;
  newName: LocalizedValue;
  error: string | null;
  submitting: boolean;
}

const initialCloneDialogState: CloneDialogState = {
  visible: false,
  sourceContext: null,
  newName: { default: '', translations: {} },
  error: null,
  submitting: false,
};

const localizedStringToValue = (ls: LocalizedString | undefined): LocalizedValue => {
  if (!ls) return { default: '' };
  return { default: ls.default ?? '', translations: ls.translations };
};

// --- Component ---

export interface ContextListPageProps {
  /** Studio host (EAINT-862): suppresses the app `Header`/`MobileHeader`/`MobileNavBar` — the
   * host's own chrome (`StudioShell`) replaces them. Mobile alias keeps the default (chrome
   * shown) since it is not hosted inside Studio. */
  hideChrome?: boolean;
  /** Builds the edit route for a context — defaults to the legacy `/contexts/:sn/edit` shape.
   * The Studio host overrides this to `/studio/context/:sn`. */
  editPath?: (systemName: string) => string;
  /** Overrides the New action — when provided, called instead of navigating to
   * `/contexts/new` (the Studio gallery opens its own "New…" menu instead). Receives
   * the triggering click event (PrimeReact's `Button` forwards it positionally even
   * though the organism's own `onNew` prop is typed niladic) so a popup menu can
   * anchor itself to the button that opened it. */
  onNewClick?: (event?: unknown) => void;
}

const DEFAULT_EDIT_PATH = (systemName: string): string => `/contexts/${systemName}/edit`;

const ContextListPage: React.FC<ContextListPageProps> = ({
  hideChrome = false,
  editPath = DEFAULT_EDIT_PATH,
  onNewClick,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const defaultLocale = process.env.REACT_APP_I18N_LOCALE || 'en-US';
  const userLocale = useSelector((s: RootState) => s.userProfile.locale ?? defaultLocale);
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const currentSphere = useSelector((state: RootState) => state.userProfile?.userSphere);
  const organizationId = useSelector(
    (state: RootState) => state.userProfile?.userSphere?.properties?.sphereId
  );
  const metaspaces = useSelector(selectAllMetaspaces);

  const apiClients = useApiClientsOrNull();
  const schemaClient = apiClients?.schemaClient;

  const contexts = useSelector(selectContextsList);
  const loading = useSelector(selectContextsLoading);
  const error = useSelector(selectContextsError);

  const [cloneDialog, setCloneDialog] = useState<CloneDialogState>(initialCloneDialogState);

  // Fetch contexts on mount
  useEffect(() => {
    if (!schemaClient || !organizationId) return;
    dispatch(fetchContexts({ apiClient: schemaClient }));
  }, [schemaClient, organizationId, dispatch]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      dispatch(clearContextsError());
    };
  }, [dispatch]);

  // --- Navigation handlers ---

  const handleNew = useCallback((event?: unknown) => {
    if (onNewClick) {
      onNewClick(event);
      return;
    }
    navigate('/contexts/new');
  }, [navigate, onNewClick]);

  const handleEdit = useCallback(
    (context: Context) => {
      navigate(editPath(context.system_name));
    },
    [navigate, editPath]
  );

  const handleDelete = useCallback(
    (context: Context) => {
      if (!schemaClient || !organizationId) return;

      const contextName =
        context.name?.translations?.[i18n.language] ||
        context.name?.default ||
        context.system_name;

      confirmDialogAtom({
        header: t('context.listPage.delete.title'),
        message: t('context.listPage.delete.message', { name: contextName }),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: t('context.listPage.delete.confirm'),
        rejectLabel: t('common.actions.cancel'),
        variant: 'danger',
        accept: () => {
          dispatch(
            deleteContextThunk({
              apiClient: schemaClient,
              systemName: context.system_name,
            })
          )
            .unwrap()
            .catch((err) => {
              console.error('[ContextListPage] Delete failed:', err);
            });
        },
      });
    },
    [schemaClient, organizationId, dispatch, t, i18n.language]
  );

  // --- Clone handlers ---

  const handleCloneClick = useCallback(
    (context: Context) => {
      const copySuffix = t('context.listPage.clone.copySuffix');
      setCloneDialog({
        visible: true,
        sourceContext: context,
        newName: {
          default: `${context.name?.default || ''} ${copySuffix}`,
          translations: Object.fromEntries(
            Object.entries(context.name?.translations || {}).map(([locale, value]) => [
              locale,
              `${value} ${copySuffix}`,
            ])
          ),
        },
        error: null,
        submitting: false,
      });
    },
    [t]
  );

  const handleCloneDialogHide = useCallback(() => {
    setCloneDialog(initialCloneDialogState);
  }, []);

  const handleCloneSubmit = useCallback(async () => {
    if (!schemaClient || !cloneDialog.sourceContext || !organizationId) return;

    const { newName, sourceContext } = cloneDialog;

    if (!newName.default.trim()) {
      setCloneDialog((prev) => ({
        ...prev,
        error: t('context.listPage.clone.errors.nameRequired'),
      }));
      return;
    }

    setCloneDialog((prev) => ({ ...prev, submitting: true, error: null }));

    try {
      const clonedContext = await dispatch(
        cloneContextThunk({
          apiClient: schemaClient,
          systemName: sourceContext.system_name,
          payload: {
            newName: {
              default: newName.default.trim(),
              translations: newName.translations,
            },
          },
        })
      ).unwrap();

      setCloneDialog(initialCloneDialogState);
      dispatch(fetchContexts({ apiClient: schemaClient }));
      navigate(editPath(clonedContext.system_name));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setCloneDialog((prev) => ({
        ...prev,
        submitting: false,
        error: msg,
      }));
    }
  }, [schemaClient, cloneDialog, organizationId, dispatch, t, navigate, editPath]);

  const handleRefresh = useCallback(() => {
    if (!schemaClient || !organizationId) return;
    dispatch(fetchContexts({ apiClient: schemaClient }));
  }, [schemaClient, organizationId, dispatch]);

  // --- Mobile navigation ---

  const handleAtlasClick = useCallback(() => {
    if (userId) {
      navigate(`/sphere/${userId}/metaspace/00000000-0000-0000-0000-000000000001`);
    }
  }, [userId, navigate]);

  const handleSettingsMenuClick = useCallback(() => {
    if (!userId) return;
    const identityMetaspace = metaspaces?.find(
      (ms: { system_name: string }) => ms.system_name === 'center_identity_management_metaspace'
    );
    if (identityMetaspace?.metaspace_id) {
      navigate(`/sphere/${userId}/metaspace/${identityMetaspace.metaspace_id}`);
    }
  }, [userId, metaspaces, navigate]);

  const navItems = useMemo(
    () => [
      {
        id: 'home',
        icon: 'Home-icon',
        label: t('common.navigation.home'),
        onClick: () => navigate('/'),
        isActive: location.pathname === '/',
      },
      {
        id: 'atlas',
        icon: 'Atlas-icon',
        label: t('common.navigation.atlas'),
        onClick: handleAtlasClick,
      },
      {
        id: 'contexts',
        icon: 'Database-icon',
        label: t('context.inventory.title'),
        onClick: () => navigate('/contexts'),
        isActive: location.pathname.startsWith('/contexts'),
      },
      {
        id: 'menu',
        icon: 'Settings-icon',
        label: t('common.navigation.settings'),
        onClick: handleSettingsMenuClick,
      },
    ],
    [navigate, location.pathname, handleAtlasClick, handleSettingsMenuClick, t]
  );

  const cloneDialogFooter = useMemo(
    () => (
      <div className="context-list-page__clone-dialog-footer">
        <Button
          label={t('common.actions.cancel')}
          className="p-button-text"
          onClick={handleCloneDialogHide}
          disabled={cloneDialog.submitting}
        />
        <Button
          label={t('context.listPage.clone.confirm')}
          icon={cloneDialog.submitting ? 'pi pi-spin pi-spinner' : 'pi pi-copy'}
          onClick={handleCloneSubmit}
          disabled={cloneDialog.submitting}
        />
      </div>
    ),
    [t, handleCloneDialogHide, handleCloneSubmit, cloneDialog.submitting]
  );

  if (!userId) {
    return (
      <div className="context-list-page__loading">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {!hideChrome && (
        <Suspense fallback={<div className="mobile-component-loading" />}>
          <MobileHeader
            logoSrc={logo}
            logoAlt={t('common.logo.alt')}
            onLogoClick={() => navigate('/')}
            placeholder={t('context.inventory.searchPlaceholder')}
          />
        </Suspense>
      )}
      {!hideChrome && <Header sphereId={userId} defaultLanguage={userLocale} sphere={currentSphere} />}
      {/* Host for the imperative confirmDialogAtom() used by delete — without a
          mounted host, the confirm never appears and delete does nothing. */}
      <ConfirmDialogAtom />
      <div className="context-list-page">
        <div className="context-list-page__container">
          {error && (
            <Message
              severity="error"
              text={error}
              className="context-list-page__error"
              data-testid="context-list-error"
            />
          )}
          <ContextInventoryOrganism
            contexts={contexts}
            loading={loading}
            onNew={handleNew}
            onEdit={handleEdit}
            onClone={handleCloneClick}
            onDelete={handleDelete}
            onRefresh={handleRefresh}
            locale={userLocale}
          />
        </div>
      </div>
      {!hideChrome && (
        <Suspense fallback={<div className="mobile-component-loading" />}>
          <MobileNavBar items={navItems} position="bottom" />
        </Suspense>
      )}

      {/* Clone Dialog */}
      <CustomDialog
        header={t('context.listPage.clone.title')}
        visible={cloneDialog.visible}
        onHide={handleCloneDialogHide}
        footer={cloneDialogFooter}
        style={{ width: '500px', maxWidth: '90vw' }}
        className="context-list-page__clone-dialog"
      >
        <div className="context-list-page__clone-form">
          {cloneDialog.error && (
            <Message
              severity="error"
              text={cloneDialog.error}
              className="context-list-page__clone-error"
            />
          )}
          <div className="context-list-page__clone-field">
            <LocalizedInputAtom
              value={cloneDialog.newName}
              onChange={(newName) =>
                setCloneDialog((prev) => ({
                  ...prev,
                  newName,
                  error: null,
                }))
              }
              label={t('context.listPage.clone.nameLabel')}
              placeholder={t('context.listPage.clone.namePlaceholder')}
              disabled={cloneDialog.submitting}
            />
          </div>
        </div>
      </CustomDialog>
    </>
  );
};

export default ContextListPage;
